// Eskom PARCC — the evidence viewer (AI_ADDENDUM.md §3.4).
//
// Opened by any citation chip. Shows the cited document at the CITED PAGE, with
// the quoted span highlighted on the rendered page.
//
// WHY THIS IS NOT A ONE-LINE EMBED. Most of the pack is SCANNED — that is the
// whole reason the engine OCRs it — so a scanned page has no text layer for a
// PDF viewer to search or select. The highlight therefore cannot come from
// PDF.js at all: it comes from the token bounding boxes Document AI produced,
// cached beside the text as a `.pages.json` sidecar, and matched to the quote by
// its character offset. That is the same machinery that DERIVES the page number
// rather than trusting the engine's claim, so a citation and its highlight can
// never disagree.
//
// The document itself is fetched from a short-lived signed URL: two files in
// this pack are 51-52 MB, and streaming those through Cloud Run would put the
// whole thing inside the ~60s Hosting ceiling for a viewer that renders one page.
import { useCallback, useEffect, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Flyout, Button } from '../../components/ui';
import { adminApi, type AiProposal } from '../../lib/adminApi';

/** A citation, plus the rationale it supports. `interface … extends` cannot
 *  extend an indexed access type, so this is a type alias intersection. */
export type EvidenceCitation = AiProposal['citations'][number] & {
  /** The rationale the citation supports, shown in the accent callout. */
  rationale?: string;
};

type Load =
  | { kind: 'loading' }
  | { kind: 'ready'; url: string }
  | { kind: 'error'; message: string };

export function EvidenceViewer({
  open,
  onClose,
  referenceNo,
  citation,
}: {
  open: boolean;
  onClose: () => void;
  referenceNo: string;
  citation: EvidenceCitation | null;
}): React.JSX.Element | null {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [rendering, setRendering] = useState(false);

  const doc = citation?.doc ?? '';
  const page = citation?.page ?? 1;

  useEffect(() => {
    if (!open || doc === '') return;
    let cancelled = false;
    setLoad({ kind: 'loading' });
    void (async () => {
      try {
        const signed = await adminApi.aiEvidenceUrl(referenceNo, doc);
        if (cancelled) return;
        setLoad({ kind: 'ready', url: signed.url });
      } catch (err) {
        if (cancelled) return;
        setLoad({
          kind: 'error',
          message: err instanceof Error ? err.message : 'That document could not be opened.',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, referenceNo, doc]);

  /** Render the cited page and paint the highlight over it. */
  const draw = useCallback(
    async (url: string) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      setRendering(true);
      try {
        // Imported lazily: pdfjs is a large dependency and only a reviewer who
        // actually opens a citation should pay for it.
        const pdfjs = await import('pdfjs-dist');
        // The worker is bundled by Vite rather than fetched from a CDN — the
        // page's CSP has no external origins, and an unpinned CDN worker would
        // be a third party executing over NDA-covered documents.
        const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
        pdfjs.GlobalWorkerOptions.workerSrc = worker.default;

        const pdf = await pdfjs.getDocument({ url }).promise;
        const clamped = Math.min(Math.max(1, page), pdf.numPages);
        const pdfPage = await pdf.getPage(clamped);

        // Render at 2x for legibility on the small print these verdicts turn on
        // (certificate numbers, SARS PINs, stamped dates).
        const viewport = pdfPage.getViewport({ scale: 2 });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        await pdfPage.render({ canvas, canvasContext: ctx, viewport }).promise;

        // The highlight. Boxes come from Document AI via the server, resolved
        // from the SAME offsets that derived the page number — a scanned page
        // has no text layer for pdfjs to search, and re-deriving them here would
        // let the highlight and the page disagree.
        const boxes = citation?.boxes ?? [];
        if (boxes.length > 0) {
          ctx.save();
          ctx.fillStyle = 'rgba(255, 193, 7, 0.32)';
          ctx.strokeStyle = 'rgba(214, 158, 0, 0.85)';
          ctx.lineWidth = 1.5;
          for (const b of boxes) {
            const x = b.x0 * canvas.width;
            const y = b.y0 * canvas.height;
            const w = (b.x1 - b.x0) * canvas.width;
            const h = (b.y1 - b.y0) * canvas.height;
            ctx.fillRect(x, y, w, h);
            ctx.strokeRect(x, y, w, h);
          }
          ctx.restore();
        }
      } catch {
        setLoad({
          kind: 'error',
          message: 'This document could not be rendered. Use “Open source” to view it directly.',
        });
      } finally {
        setRendering(false);
      }
    },
    [page, citation?.boxes],
  );

  useEffect(() => {
    if (load.kind !== 'ready') return;
    void draw(load.url);
  }, [load, draw]);

  if (!open || !citation) return null;

  return (
    <Flyout
      open={open}
      onClose={onClose}
      title={citation.doc.split('/').pop() ?? citation.doc}
      subtitle={`Evidence · africa-south1 · page ${citation.page}`}
      width="narrow"
      ariaLabel={`Evidence: ${citation.doc}, page ${citation.page}`}
      headerExtra={
        load.kind === 'ready' ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open(load.url, '_blank', 'noopener,noreferrer')}
          >
            <ExternalLink size={13} aria-hidden /> Open source
          </Button>
        ) : undefined
      }
    >
      {/* The cited span, in the accent callout — this is the claim the verdict
          rests on, and it is verbatim from the extracted text, not a summary. */}
      <div className="ai-ev-cited">
        <div className="ai-ev-cited-h">Cited text</div>
        <blockquote className="ai-ev-quote">{citation.quote}</blockquote>
        {citation.rationale && <div className="small">{citation.rationale}</div>}
      </div>

      <div className="ai-ev-page" aria-live="polite">
        {load.kind === 'loading' && <div className="small">Opening the document…</div>}
        {load.kind === 'error' && (
          <div className="ai-bad small" role="alert">
            {load.message}
          </div>
        )}
        {rendering && load.kind === 'ready' && <div className="small">Rendering page {page}…</div>}
        <canvas
          ref={canvasRef}
          className="ai-ev-canvas"
          // The highlight is decorative duplication of the quote above, which is
          // already in the accessibility tree — announcing the canvas would only
          // repeat it without the text.
          role="img"
          aria-label={`Page ${citation.page} of ${citation.doc}, with the cited passage highlighted`}
        />
      </div>
    </Flyout>
  );
}
